import './CustomStatistics.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import moment from 'moment';

import { getAlarmStats, getCriticalAlarmsWithoutNotification, getAlarmHistoricalSummary, getAlarmStatsLoading } from 'src/module/alarm/selector';
import { getMetrics } from 'src/module/facility/selector';
import { getDashboardStats } from 'src/module/device/selector';

import { getNewWorkTickets } from 'src/module/workTicket/selector';
import { getClient, getPluralWorkTicketLabel, getCriticalityLabels } from 'src/module/authentication/selector';
import CustomStatistics from './CustomStatistics';

export default function DashboardStatistics (props) {

  const {
    defaultLayoutOverride,
  } = props;

  const client = useSelector(getClient);
  const alarmStats = useSelector(getAlarmStats);
  const metrics = useSelector(getMetrics);
  const dashboardStats = useSelector(getDashboardStats);
  const criticalAlarmsWithoutNotification = useSelector(getCriticalAlarmsWithoutNotification);
  const newWorkTickets = useSelector(getNewWorkTickets) as any;
  const alarmHistory = useSelector(getAlarmHistoricalSummary) as any;
  const pluralWorkTicketLabel = useSelector(getPluralWorkTicketLabel);
  const criticalityLabels = useSelector(getCriticalityLabels);
  const alarmStatsLoading = useSelector(getAlarmStatsLoading);
  const lastReportSentAt = client.get('last_report_sent_at');
  const currentTemp = alarmStats.getIn(['currentSiteTemperature', 'current'], null);

  let circuitsLoto = null;

  const outstandingWorkOrders = alarmStats.getIn(['outstandingNotificationsByStatus', 'wo'], 0) + alarmStats.getIn(['outstandingNotificationsByStatus', 'eng_required'], 0);
  if (alarmStats) {
    circuitsLoto = alarmStats.getIn(['notesByHtc', 'loto']);
  }
  const variables = {
    alarmStats,
    alarmStatsLoading,
    client,
    metrics,
    totalHoursSavedToday: alarmHistory.getIn([alarmHistory.size - 1, 'total_auto_handled'], 'N/A'),
    totalNewNotifications: newWorkTickets.size,
    criticalAlarmsWithoutNotification,
    circuitsLoto,
    outstandingWorkOrders,
    date: moment().format('YYYY-MM-DD'),
    lastReportSentAt: lastReportSentAt ? moment(lastReportSentAt).format('YYYY-MM-DD') : 'N/A',
    currentSiteTemperature: currentTemp ? `${currentTemp}°C` : 'N/A',
    weekNumber: moment().isoWeek(),
    dashboardStats
  };

  const interpolatedVariables = {
    '%estTotalMaintenanceHoursExplain%': {
      valuePath: 'alarmStats.estTotalMaintenaceHoursExplain',
      default: 'Total estimated maintenance hours'
    },
    '%estBasicTotalMaintenanceHoursExplain%': {
      valuePath: 'alarmStats.estBasicTotalMaintenaceHoursExplain',
      default: 'Total estimated maintenance hours'
    },
    '%mnraLabel%': {
      valuePath: 'client.preferences.notificationLabels.mnra',
      default: 'EHT Walk Downs'
    },
    '%engRequiredLabel%': {
      valuePath: 'client.preferences.notificationLabels.eng_required',
      default: `Eng Req'd`
    },
    '%workOrderLabel%': {
      valuePath: 'client.preferences.notificationLabels.wo',
      default: 'Scheduled Repairs'
    },
  };
  const devicesInAlarmWithoutNotifications = [];
  alarmStats?.get('devicesInAlarmWithoutNotificationByCriticality', []).forEach((value,  key) => {
    if (key > 0) {
      devicesInAlarmWithoutNotifications.push({
        type: 'minor',
        title: criticalityLabels?.get(key),
        helpText: `Number of priority '${criticalityLabels?.get(key)}' devices in alarm without ${pluralWorkTicketLabel}`,
        value: `alarmStats.devicesInAlarmWithoutNotificationByCriticality.${key}`,
        defaultValue: 'N/A'
      });
    }
  });

  const dynamicChildren = {
    devicesInAlarmWithoutNotifications
  };

  const defaultLayout = [
    { "type": "grid", "container": true, "spacing": 3, "children": [
      { "type": "grid", "container": false, "md": 12, "xs": 12, "children": [
        { "type": "component", "componentName": "WeatherForecast" }
      ] },

      { "type": "grid", "container": false, "md": 12, "xs": 12, "children": [
        { "type": "block", "title": "Outlook", "children": [
          { "type": "major", "title": "Critical Alarms w/o Notification", "value": "alarmStats.criticalAlarmsWithoutNotification", "helpText": "Total number of devices in critical alarm and no associated notification" },
          { "type": "major", "title": "New Notifications in Last 7 Days", "value": "totalNewNotifications", "helpText": "Total SAP notifications created in the last 7 days" },
          { "type": "major", "title": "Circuits Forced On", "value": "alarmStats.deviceStatusSummary.forced_on", "defaultValue": "N/A" },
          { "type": "major", "title": "Field Repairs & Assessments", "value": "alarmStats.outstandingNotificationsByStatus.mnra", "helpText": "Circuits that require field assessment", "defaultValue": 0 },
          { "type": "major", "title": "Total Estimated EHT Maintenance", "value": "alarmStats.estBasicTotalMaintenaceHours", "helpText": "Based on site's average time to complete MNRA and WO and predictive failures: %estBasicTotalMaintenanceHoursExplain%", "defaultValue": 0 },
          { "type": "major", "title": "Total kWh", "value": "dashboardStats.kwh", "helpText": "Sum of the All-time kWh for all devices or devices that match the filter applied", "defaultValue": 0, "dynamicTitle": "dynamickWhTitle", "formatFunction": "formatkWh", "dynamicHelpText": "dynamickWhHelpText" },
          { "type": "major", "title": "Total Wasted", "value": "dashboardStats.forcedOnKwh", "helpText": "Sum of kWh used by devices they were forced on", "defaultValue": "-", "dynamicTitle": "dynamickWhForcedOnTitle", "formatFunction": "formatkWh", "dynamicHelpText": "dynamicForcedOnkWhHelpText" }
        ] }
      ] },

      { "type": "grid", "container": false, "md": 4, "xs": 12, "children": [
        { "type": "block", "title": "Alarm Overview", "children": [
          { "type": "minor", "title": "Total Alarms", "value": "alarmStats.totalAlarms", "helpText": "Total active alarms in the system", "defaultValue": "N/A" },
          { "type": "minor", "title": "HTCs in Alarm", "value": "alarmStats.htcsInAlarm", "helpText": "Total Heat Trace Controllers in alarm", "defaultValue": "N/A" },
          { "type": "minor", "title": "HTCs in Crit. Alarm", "value": "alarmStats.criticalAlarms", "helpText": "Total Heat Trace Controllers in critical alarm", "defaultValue": "N/A" }
        ] }
      ] },

      { "type": "grid", "container": false, "md": 4, "xs": 12, "children": [
        { "type": "block", "title": "System Overview", "children": [
          { "type": "minor", "title": "Nuisance Alarms", "value": "alarmStats.notesByHtc.nuisance", "helpText": "Total number of alarms determined to be a nuisance alarm", "defaultValue": "N/A" },
          { "type": "minor", "title": "LOTO", "value": "alarmStats.notesByHtc.loto", "helpText": "Total number of alarms resulting from device being Locked Out / Tagged Out", "defaultValue": "N/A" },
          { "type": "minor", "title": "Offline Devices", "value": "alarmStats.deviceStatusSummary.offline", "helpText": "Total number of alarms resulting from device being offline", "defaultValue": "N/A" }
        ] }
      ] },

      { "type": "grid", "container": false, "md": 4, "xs": 12, "children": [
        { "type": "block", "title": "ERP Overview", "children": [
          { "type": "minor", "title": "%mnraLabel%", "value": "alarmStats.outstandingNotificationsByStatus.mnra", "helpText": "Total open SAP tickets requiring engineering", "defaultValue": "N/A" },
          { "type": "minor", "title": "%engRequiredLabel%", "value": "alarmStats.outstandingNotificationsByStatus.eng_required", "helpText": "Total open SAP Work Orders", "defaultValue": "N/A" },
          { "type": "minor", "title": "%workOrderLabel%", "value": "alarmStats.totalNotificationsWithWorkOrders", "helpText": "Total open SAP notifications for an approved minor repair", "defaultValue": "N/A" }
        ] }
      ] },

      { "type": "grid", "container": false, "md": 12, "xs": 12, "children": [
        { "type": "block", "title": "EHT Sensor History", "children": [
          { "type": "component", "componentName": "SensorHistory" }
        ] }
      ] },

      { "type": "grid", "container": false, "md": 6, "xs": 12, "children": [
        { "type": "block", "title": "Work Completed", "children": [
          { "type": "component", "componentName": "WorkCompletedGraph" }
        ] }
      ] },

      { "type": "grid", "container": false, "md": 6, "xs": 12, "children": [
        { "type": "block", "title": "Alarms by Status", "children": [
          { "type": "component", "componentName": "AlarmsByStatusGraph" }
        ] }
      ] },

      { "type": "grid", "container": false, "md": 6, "xs": 12, "children": [
        { "type": "block", "title": "New Work Tickets", "children": [
          { "type": "component", "componentName": "NewWorkTickets" }
        ] }
      ] },

      { "type": "grid", "container": false, "md": 6, "xs": 12, "children": [
        { "type": "block", "title": "Critical Alarms", "children": [
          { "type": "component", "componentName": "CriticalAlarms" }
        ] }
      ] }
    ] }
  ];

  return (
    <CustomStatistics
      layoutPreferencePath={props.layoutPreferencePath || 'client.preferences.dashboard.layout'}
      variables={variables}
      interpolatedVariables={interpolatedVariables}
      defaultLayout={defaultLayoutOverride ? defaultLayoutOverride : defaultLayout}
      dynamicChildren={dynamicChildren}
    />
  );
}

DashboardStatistics.propTypes = {
  layoutPreferencePath: PropTypes.string,
  defaultLayoutOverride: PropTypes.array,
};

DashboardStatistics.defaultProps = {
  layoutPreferencepPath: null,
  defaultLayoutOverride: null
};
